import React, { useState, useEffect } from 'react';
import { useOutletContext, useParams, useNavigate } from 'react-router-dom';
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Tooltip
} from '@mui/material';
import PageSnackbars from '../common/PageSnackbars';
import { usePageSnackbar } from '../common/usePageSnackbar';
import { acme } from '../../theme/acmeTheme';
import { PageShell } from '../ui';
import axios from 'axios';
import FileDownload from 'js-file-download';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { isAdminRole } from '../../utils/userRoles';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'bold'
}));

function maskValue(value) {
  if (!value) return '';
  if (value.includes('@')) {
    const [left, domain] = value.split('@');
    const leftPart = left.slice(0, 2);
    const leftMask = '*'.repeat(Math.max(0, left.length - 2));
    const domainPart = domain.slice(0, 5);
    const domainMask = '*'.repeat(Math.max(0, domain.length - 5));
    return `${leftPart}${leftMask}@${domainPart}${domainMask}`;
  } else {
    if (value.length <= 4) return value;
    return `${value.slice(0, 4)}****${value.slice(-3)}`;
  }
}

const ParsingDetail = () => {
  const { authenticated, user } = useOutletContext();
  const isAdmin = isAdminRole(user?.role);
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setSuccessMessage, setErrorMessage, snackbarProps } = usePageSnackbar();
  
  // States for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  useEffect(() => {
    const fetchSession = async () => {
      try {
        const config = { withCredentials: true };
        // Если пользователь не аутентифицирован (гость), передаем токен из guestSessionToken
        if (!authenticated) {
          const token = localStorage.getItem('guestSessionToken');
          if (token) {
            config.headers = { 'x-guest-token': token };
          }
        }
        const res = await axios.get(`/api/parsing/sessions/${id}`, config);
        setSession(res.data);
      } catch (error) {
        console.error('Error fetching session:', error);
        setErrorMessage('Failed to fetch session details.');
      } finally {
        setLoading(false);
      }
    };
    fetchSession();
    // Intentional: load session when route id/auth changes; setErrorMessage is stable setter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, authenticated]);
  
  const handleExport = async () => {
    try {
      const config = { responseType: 'blob' };
      if (!authenticated) {
        const token = localStorage.getItem('guestSessionToken');
        if (token) {
          config.headers = { 'x-guest-token': token };
        }
      }
      const response = await axios.get(`/api/parsing/sessions/${id}/export`, config);
      FileDownload(response.data, `parsing_session_${id}.xlsx`);
      setSuccessMessage('Export success');
    } catch (error) {
      console.error('Export error:', error);
      setErrorMessage('Export failed.');
    }
  };
  
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }
  
  if (!session) {
    return <Typography variant="h6">Session not found.</Typography>;
  }

  // Calculate export button state
  let isExportDisabled = false;
  let exportButtonVariant = 'outlined';
  let exportButtonColor = 'inherit';

  if (!isAdmin) {
    // Logic for non-admin users
    isExportDisabled = session.paymentStatus !== 'Paid';
    exportButtonVariant = !isExportDisabled ? 'contained' : 'outlined';
    exportButtonColor = !isExportDisabled ? 'success' : 'inherit';
  }

  // Pagination logic for results
  const results = session.results || [];
  const paginatedResults = results.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  return (
    <PageShell>
      <PageSnackbars {...snackbarProps} />

      <Paper sx={{ p: 2, maxWidth: '100%', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: acme.colors.brand,
              '&:hover': {
                backgroundColor: acme.colors.brandMuted,
              },
            }}
          >
            Back to Parsing
          </Button>
          <Typography variant="h5" align="center">
            Parsing Detail: {session.parsingName}
          </Typography>
          <Tooltip
            title={
              isExportDisabled
                ? 'Button will become active after payment'
                : ''
            }
          >
            <span>
              <Button
                variant={exportButtonVariant}
                color={exportButtonColor}
                onClick={handleExport}
                disabled={isExportDisabled}
              >
                Export
              </Button>
            </span>
          </Tooltip>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body1"><strong>Scraping Method:</strong> {session.parsingMethod}</Typography>
          <Typography variant="body1"><strong>Source Type:</strong> {session.sourceType}</Typography>
          <Typography variant="body1" sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            <strong>Source Detail:</strong> {session.sourceDetail}
          </Typography>
          <Typography variant="body1"><strong>Payment Status:</strong> {session.paymentStatus}</Typography>
        </Box>
        
        <Typography variant="h6" gutterBottom>
          Results ({results.length})
        </Typography>
        
        {results.length === 0 ? (
          <Typography>No results found.</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ mt: 1, maxWidth: '100%', overflowX: 'auto' }}>
            <Table sx={{ tableLayout: 'fixed', minWidth: 400 }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>#</StyledTableCell>
                  <StyledTableCell>Result</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedResults.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      {isAdmin || session.paymentStatus === 'Paid' ? item : maskValue(item)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={results.length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      const newVal = parseInt(event.target.value, 10);
                      setRowsPerPage(newVal);
                      setPage(0);
                    }}
                    rowsPerPageOptions={[20, 50, 100, 200]}
                    labelRowsPerPage="Rows per page"
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </PageShell>
  );
};

export default ParsingDetail;
